import { Spinner, lazyWithRetry } from '@ps/ui';
import { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { RedirectIfSignedIn } from '../features/auth/RedirectIfSignedIn';
import { RequireAuth } from '../features/auth/RequireAuth';
import { AppShell } from './AppShell';

// Routes are split so the first paint ships only what the landing screen needs.
const HomePage = lazyWithRetry('HomePage', () => import('../pages/HomePage'));
const SearchResultsPage = lazyWithRetry(
  'SearchResultsPage',
  () => import('../pages/SearchResultsPage'),
);
const RideDetailsPage = lazyWithRetry('RideDetailsPage', () => import('../pages/RideDetailsPage'));
const LoginPage = lazyWithRetry('LoginPage', () => import('../pages/auth/LoginPage'));
const VerifyOtpPage = lazyWithRetry('VerifyOtpPage', () => import('../pages/auth/VerifyOtpPage'));
const RegisterPage = lazyWithRetry('RegisterPage', () => import('../pages/auth/RegisterPage'));
const ProfilePage = lazyWithRetry('ProfilePage', () => import('../pages/auth/ProfilePage'));
const SecurityPage = lazyWithRetry('SecurityPage', () => import('../pages/auth/SecurityPage'));
const BookRidePage = lazyWithRetry('BookRidePage', () => import('../pages/bookings/BookRidePage'));
const BookingDetailPage = lazyWithRetry(
  'BookingDetailPage',
  () => import('../pages/bookings/BookingDetailPage'),
);
const MyBookingsPage = lazyWithRetry(
  'MyBookingsPage',
  () => import('../pages/bookings/MyBookingsPage'),
);
const NotificationsPage = lazyWithRetry(
  'NotificationsPage',
  () => import('../pages/notifications/NotificationsPage'),
);
const TripsPage = lazyWithRetry('TripsPage', () => import('../pages/trips/TripsPage'));
const MessagesPage = lazyWithRetry('MessagesPage', () => import('../pages/messages/MessagesPage'));
const SettingsPage = lazyWithRetry('SettingsPage', () => import('../pages/settings/SettingsPage'));
const SafetyPage = lazyWithRetry('SafetyPage', () => import('../pages/safety/SafetyPage'));
const HowItWorksPage = lazyWithRetry(
  'HowItWorksPage',
  () => import('../pages/public/HowItWorksPage'),
);
const RoutesPage = lazyWithRetry('RoutesPage', () => import('../pages/public/RoutesPage'));
const RouteLandingPage = lazyWithRetry(
  'RouteLandingPage',
  () => import('../pages/public/RouteLandingPage'),
);
const SafetyInfoPage = lazyWithRetry(
  'SafetyInfoPage',
  () => import('../pages/public/SafetyInfoPage'),
);
const FaqPage = lazyWithRetry('FaqPage', () => import('../pages/public/FaqPage'));
const ContactPage = lazyWithRetry('ContactPage', () => import('../pages/public/ContactPage'));
const LegalDocumentPage = lazyWithRetry(
  'LegalDocumentPage',
  () => import('../pages/public/LegalDocumentPage'),
);
const TripDetailPage = lazyWithRetry(
  'TripDetailPage',
  () => import('../pages/trips/TripDetailPage'),
);
const ChatPage = lazyWithRetry('ChatPage', () => import('../pages/chat/ChatPage'));
const NotFoundPage = lazyWithRetry('NotFoundPage', () => import('../pages/NotFoundPage'));

export function App() {
  return (
    <AppShell>
      <Suspense
        fallback={
          <div className="flex justify-center py-16 text-primary">
            <Spinner size="lg" />
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<HomePage />} />

          {/* Searching and browsing are open: people compare fares before they
              ever create an account. */}
          {/* Open to anyone: these answer the questions somebody has before
              they would think about signing in. */}
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/routes" element={<RoutesPage />} />
          <Route path="/safety-info" element={<SafetyInfoPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<LegalDocumentPage document="terms" />} />
          <Route path="/privacy" element={<LegalDocumentPage document="privacy" />} />
          <Route
            path="/cancellation-policy"
            element={<LegalDocumentPage document="cancellation" />}
          />
          <Route path="/refund-policy" element={<LegalDocumentPage document="refund" />} />
          <Route
            path="/community-guidelines"
            element={<LegalDocumentPage document="guidelines" />}
          />
          <Route path="/safety-policy" element={<LegalDocumentPage document="safety" />} />
          <Route path="/cookie-policy" element={<LegalDocumentPage document="cookies" />} />
          <Route path="/disclaimer" element={<LegalDocumentPage document="disclaimer" />} />
          <Route path="/grievance" element={<LegalDocumentPage document="grievance" />} />
          {/* The console publishes by slug, so anything added there is
              reachable without a deployment. The named routes above stay as the
              addresses already printed in the app and linked from elsewhere. */}
          <Route path="/legal/:slug" element={<LegalDocumentPage />} />
          <Route path="/search" element={<SearchResultsPage />} />
          <Route path="/rides/:rideId" element={<RideDetailsPage />} />

          {/* Sign-in screens: a signed-in person is sent on instead. */}
          <Route
            path="/login"
            element={
              <RedirectIfSignedIn>
                <LoginPage />
              </RedirectIfSignedIn>
            }
          />
          <Route
            path="/verify-otp"
            element={
              <RedirectIfSignedIn>
                <VerifyOtpPage />
              </RedirectIfSignedIn>
            }
          />

          {/* Signed-in screens. The API authorises every request regardless. */}
          <Route
            path="/register"
            element={
              <RequireAuth>
                <RegisterPage />
              </RequireAuth>
            }
          />
          {/* Booking needs an account: a seat has to belong to somebody. */}
          <Route
            path="/rides/:rideId/book"
            element={
              <RequireAuth>
                <BookRidePage />
              </RequireAuth>
            }
          />
          <Route
            path="/bookings"
            element={
              <RequireAuth>
                <MyBookingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/bookings/:bookingId"
            element={
              <RequireAuth>
                <BookingDetailPage />
              </RequireAuth>
            }
          />

          <Route
            path="/messages"
            element={
              <RequireAuth>
                <MessagesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/safety"
            element={
              <RequireAuth>
                <SafetyPage />
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <SettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/trips"
            element={
              <RequireAuth>
                <TripsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/trips/:bookingId"
            element={
              <RequireAuth>
                <TripDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/notifications"
            element={
              <RequireAuth>
                <NotificationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/bookings/:bookingId/chat"
            element={
              <RequireAuth>
                <ChatPage />
              </RequireAuth>
            }
          />

          <Route
            path="/profile"
            element={
              <RequireAuth>
                <ProfilePage />
              </RequireAuth>
            }
          />
          <Route
            path="/security"
            element={
              <RequireAuth>
                <SecurityPage />
              </RequireAuth>
            }
          />

          {/* Clean, indexable route pages: /gorakhpur-to-maharajganj-carpool.
              Matched before the catch-all so a real slug never 404s. */}
          <Route path="/:slug" element={<RouteLandingPage />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}
